import React, { ChangeEvent, useEffect } from "react";
import Box from "@mui/material/Box";
import { updateFeedback } from "../../../redux/slices/AppState";
import { FEEDBACK_VARIANT } from "../../extras/Alert";
import { FEEDBACK_ALERTS } from "../../../types/common.types";
import { Dispatch } from "@reduxjs/toolkit";
import { setFileSelection } from "../../../redux/slices/ProductState";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useTranslation } from "../../../hooks/useTranslation";
import { Button, Stack, Typography } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

interface SelfUploadProps {
  title?: string;
}

const SelfUpload = ({ title }: SelfUploadProps) => {
  const { selectedFilePreview } = useSelector(
    (state: RootState) => state.Product
  );

  const dispatch = useDispatch();

  const uploadYourPhoto = useTranslation("upload_photo_title");

  useEffect(() => {
    if (selectedFilePreview) {
      setTimeout(() => {
        URL.revokeObjectURL(selectedFilePreview);
      }, 2000);
    }
  }, [selectedFilePreview]);

  const readPhotoToBase64 = (
    file: File,
    dispatch: Dispatch,
    preview: string
  ) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onloadend = () => {
      dispatch(
        setFileSelection({ file, fileInBase64: fileReader.result, preview })
      );
    };
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files;
    const pattern = /\.(jpe?g|png|svg)$/i;
    const selectedFile = file && file[0];
    if (selectedFile && pattern.test(selectedFile.name)) {
      const fileSizeInMb = (selectedFile.size / 1000000).toFixed(2);
      if (Number(fileSizeInMb) > 0.5) {
        dispatch(
          updateFeedback({
            show: true,
            type: FEEDBACK_VARIANT.ERROR,
            message: FEEDBACK_ALERTS.LARGE_FILE_SIZE,
            timeout: 4000,
          })
        );
        return;
      }
      readPhotoToBase64(
        selectedFile,
        dispatch,
        URL.createObjectURL(selectedFile)
      );
    } else {
      dispatch(
        setFileSelection({ file: null, fileInBase64: null, preview: "" })
      );
      dispatch(
        updateFeedback({
          show: true,
          type: FEEDBACK_VARIANT.ERROR,
          message: FEEDBACK_ALERTS.INVALID_FILE,
          timeout: 10000,
        })
      );
      return;
    }
  };

  return (
    <Box
      id="self-upload"
      sx={{
        width: "100%",
        border: "1px dashed rgba(131, 60, 65, 0.38)",
        borderRadius: "10px",
        bgcolor: "rgba(31, 22, 21, 0.72)",
        p: 1.5,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "8px",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(131, 60, 65, 0.18)",
              color: "#9e4a50",
              flexShrink: 0,
            }}
          >
            <CloudUploadOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{ color: "#f0ece8", fontSize: "13px", fontWeight: 700 }}
            >
              {title || uploadYourPhoto}
            </Typography>
            <Typography sx={{ color: "#7a6560", fontSize: "11px", mt: 0.2 }}>
              JPG, PNG, or SVG up to 500KB.
            </Typography>
          </Box>
        </Stack>

        <Button
          component="label"
          htmlFor="drinkImage"
          variant="contained"
          sx={{
            borderRadius: "7px",
            bgcolor: "#833c41",
            color: "#f9f3ef",
            boxShadow: "none",
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 700,
            px: 1.5,
            "&:hover": {
              bgcolor: "#9e4a50",
              boxShadow: "none",
            },
          }}
        >
          Browse
        </Button>
      </Stack>

      <input
        type="file"
        id="drinkImage"
        hidden={true}
        onChange={handleFileChange}
      />
    </Box>
  );
};

export default SelfUpload;
